import { useCallback, useMemo } from 'react';

import {
  ProcessResponseDTOStatus,
  useCancelProcess,
  useCompleteProcess,
  useHoldProcess,
  usePublishProcess,
  useResumeProcess
} from '@/api/generated';

import { useProcessPermissions } from './permissions/useProcessPermissions';

const STATUS_TRANSITION_ACTIONS = {
  PUBLISH: 'PUBLISH',
  CANCEL: 'CANCEL',
  HOLD: 'HOLD',
  RESUME: 'RESUME',
  COMPLETE_ACTIVE: 'COMPLETE_ACTIVE',
  COMPLETE_HOLD: 'COMPLETE_HOLD'
};

interface StatusTransition {
  from: ProcessResponseDTOStatus;
  to: ProcessResponseDTOStatus;
  name: string;
  handler: () => Promise<void>;
}

interface UseProcessStatusActionsOptions {
  processId: string;
  currentStatus?: ProcessResponseDTOStatus;
  onChange: (newStatus: ProcessResponseDTOStatus) => void;
}

export const useProcessStatusActions = (options: UseProcessStatusActionsOptions) => {
  const { processId, currentStatus, onChange } = options;

  const { mutateAsync: publishProcess, isLoading: isPublishLoading } = usePublishProcess();
  const { mutateAsync: cancelProcess, isLoading: isCancelLoading } = useCancelProcess();
  const { mutateAsync: holdProcess, isLoading: isHoldLoading } = useHoldProcess();
  const { mutateAsync: completeProcess, isLoading: isCompleteLoading } = useCompleteProcess();
  const { mutateAsync: resumeProcess, isLoading: isResumeLoading } = useResumeProcess();
  const isStatusLoading = isPublishLoading || isCancelLoading || isHoldLoading || isCompleteLoading || isResumeLoading;
  const mutationOptions = useMemo(() => ({ id: processId }), [processId]);

  const { value: processPermissions } = useProcessPermissions({
    variables: { processId: processId! }
  });

  const statusTransitions = useMemo(
    () =>
      (
        [
          (!processPermissions || processPermissions.canPublishProcess) && {
            from: ProcessResponseDTOStatus.UNPUBLISHED,
            to: ProcessResponseDTOStatus.ACTIVE,
            name: STATUS_TRANSITION_ACTIONS.PUBLISH,
            handler: () => publishProcess(mutationOptions)
          },
          (!processPermissions || processPermissions.canCancelProcess) && {
            from: ProcessResponseDTOStatus.UNPUBLISHED,
            to: ProcessResponseDTOStatus.CANCELLED,
            name: STATUS_TRANSITION_ACTIONS.CANCEL,
            handler: () => cancelProcess(mutationOptions)
          },
          (!processPermissions || processPermissions.canChangeToOnHold) && {
            from: ProcessResponseDTOStatus.ACTIVE,
            to: ProcessResponseDTOStatus.ON_HOLD,
            name: STATUS_TRANSITION_ACTIONS.HOLD,
            handler: () => holdProcess(mutationOptions)
          },
          (!processPermissions || processPermissions.canChangeToComplete) && {
            from: ProcessResponseDTOStatus.ACTIVE,
            to: ProcessResponseDTOStatus.COMPLETED,
            name: STATUS_TRANSITION_ACTIONS.COMPLETE_ACTIVE,
            handler: () => completeProcess(mutationOptions)
          },
          (!processPermissions || processPermissions.canChangeToOnHold) && {
            from: ProcessResponseDTOStatus.ON_HOLD,
            to: ProcessResponseDTOStatus.ACTIVE,
            name: STATUS_TRANSITION_ACTIONS.RESUME,
            handler: () => resumeProcess(mutationOptions)
          },
          (!processPermissions || processPermissions.canChangeToComplete) && {
            from: ProcessResponseDTOStatus.ON_HOLD,
            to: ProcessResponseDTOStatus.COMPLETED,
            name: STATUS_TRANSITION_ACTIONS.COMPLETE_HOLD,
            handler: () => completeProcess(mutationOptions)
          }
        ] as (StatusTransition | undefined)[]
      ).filter((transition): transition is StatusTransition => Boolean(transition)),
    [processPermissions, publishProcess, mutationOptions, cancelProcess, holdProcess, completeProcess, resumeProcess]
  );

  const availableTransitions = useMemo(
    () => statusTransitions.filter(({ from }) => from === currentStatus),
    [currentStatus, statusTransitions]
  );
  const availableStatuses = useMemo(
    () => [currentStatus, ...availableTransitions.map(({ to }) => to)],
    [availableTransitions, currentStatus]
  );
  const isFinalState = useMemo(() => !availableTransitions.length, [availableTransitions]);
  const availableActions = useMemo(() => availableTransitions.map(({ name }) => name), [availableTransitions]);

  const isPublishAvailable = useMemo(
    () => availableActions.includes(STATUS_TRANSITION_ACTIONS.PUBLISH),
    [availableActions]
  );
  const isCancelAvailable = useMemo(
    () => availableActions.includes(STATUS_TRANSITION_ACTIONS.CANCEL),
    [availableActions]
  );
  const isCompleteAvailable = useMemo(
    () =>
      availableActions.includes(STATUS_TRANSITION_ACTIONS.COMPLETE_ACTIVE) ||
      availableActions.includes(STATUS_TRANSITION_ACTIONS.COMPLETE_HOLD),
    [availableActions]
  );
  const isHoldAvailable = useMemo(() => availableActions.includes(STATUS_TRANSITION_ACTIONS.HOLD), [availableActions]);
  const isResumeAvailable = useMemo(
    () => availableActions.includes(STATUS_TRANSITION_ACTIONS.RESUME),
    [availableActions]
  );

  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      const originalStatus = currentStatus;
      try {
        const transition = availableTransitions.find(({ from, to }) => originalStatus === from && newStatus === to);
        if (!transition) {
          throw new Error(`Transition impossible: ${originalStatus} => ${newStatus}`);
        }

        onChange(newStatus as ProcessResponseDTOStatus);
        if (transition.handler) {
          await transition.handler();
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Transition is not implemented: ${transition.name}`);
        }
      } catch (e) {
        // TODO: potential for proper logging here; check out Sentry to integrate
        console.error(e); // eslint-disable-line no-console
        onChange(originalStatus as ProcessResponseDTOStatus);
      }
    },
    [availableTransitions, currentStatus, onChange]
  );

  const handlePublish = useCallback(() => {
    if (isPublishAvailable) {
      handleStatusChange(ProcessResponseDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isPublishAvailable]);

  const handleCancel = useCallback(() => {
    if (isCancelAvailable) {
      handleStatusChange(ProcessResponseDTOStatus.CANCELLED);
    }
  }, [handleStatusChange, isCancelAvailable]);

  const handleComplete = useCallback(() => {
    if (isCompleteAvailable) {
      handleStatusChange(ProcessResponseDTOStatus.COMPLETED);
    }
  }, [handleStatusChange, isCompleteAvailable]);

  const handleHold = useCallback(() => {
    if (isHoldAvailable) {
      handleStatusChange(ProcessResponseDTOStatus.ON_HOLD);
    }
  }, [handleStatusChange, isHoldAvailable]);

  const handleResume = useCallback(() => {
    if (isResumeAvailable) {
      handleStatusChange(ProcessResponseDTOStatus.ACTIVE);
    }
  }, [handleStatusChange, isResumeAvailable]);

  return {
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses,
    publish: handlePublish,
    cancel: handleCancel,
    complete: handleComplete,
    hold: handleHold,
    resume: handleResume,
    change: handleStatusChange,
    isPublishAvailable,
    isCancelAvailable,
    isCompleteAvailable,
    isHoldAvailable,
    isResumeAvailable
  };
};
